import _ from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import StationHistoryContainer from '../containers/StationHistory';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';

import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import { Redirect } from 'react-router-dom';

import FMPHeadline from '../components/ui/FMPHeadline';
import { getSharedStyles } from '../themes';
import { sortStationsByNumber } from '../libs/utility';

import moment from 'moment';

const styles = (theme) => ({
	...getSharedStyles(theme),
	controls: {
		display: 'flex',
		alignItems: 'center',
		gap: 16,
		marginBottom: 24,
		flexWrap: 'wrap',
	},
	autocompleteWrapper: {
		width: 340,
	},
	runButton: {
		height: 40,
		minWidth: 100,
	},
	copyButton: {
		marginBottom: 16,
	},
	noResults: {
		paddingTop: 12,
		fontStyle: 'italic',
		color: '#999',
	},
	oldValue: {
		color: '#c62828',
		maxWidth: 240,
		wordBreak: 'break-word',
	},
	newValue: {
		color: '#2e7d32',
		maxWidth: 240,
		wordBreak: 'break-word',
	},
	copySuccess: {
		marginLeft: 12,
		color: '#2e7d32',
		fontSize: 13,
	},
});

const COLUMNS = [
	{ label: 'Date', key: 'dateCreated' },
	{ label: 'Field', key: 'name' },
	{ label: 'Old Value', key: 'oldValue' },
	{ label: 'New Value', key: 'newValue' },
	{ label: 'Changed By', key: 'changedBy' },
];

class StationHistory extends Component {
	constructor(props) {
		super(props);
		this.state = {
			selectedStation: null,
			results: null,
			isLoading: false,
			copySuccess: false,
		};
	}

	componentDidMount() {
		if (!this.props.stations.length) {
			this.props.searchStations();
		}
	}

	handleStationChange = (event, option) => {
		this.setState({ selectedStation: option || null, results: null });
	};

	handleRun = () => {
		const { selectedStation } = this.state;
		if (!selectedStation) return;

		this.setState({ isLoading: true, results: null, copySuccess: false });

		this.props.searchStationHistory({
			params: { stationIds: [selectedStation.value] },
			callback: (err, result) => {
				if (err) {
					this.setState({ isLoading: false });
					return;
				}

				const stationHistoryIds = result.stationHistoryIds || [];
				const stationHistoryCollection = _.get(
					result,
					['collections', 'stationHistoryCollection'],
					[]
				);
				const userCollection = _.get(result, ['collections', 'userCollection'], []);
				const userMap = _.keyBy(userCollection, 'userId');
				const historyMap = _.keyBy(stationHistoryCollection, 'stationHistoryId');

				const rows = stationHistoryIds.map((id) => {
					const record = historyMap[id] || {};
					const user = userMap[record.createdByUserId] || {};
					return {
						stationHistoryId: id,
						name: _.startCase(record.name || ''),
						oldValue: record.oldValue != null ? String(record.oldValue) : '',
						newValue: record.newValue != null ? String(record.newValue) : '',
						dateCreated: record.dateCreated
							? moment(record.dateCreated).format('MM/DD/YYYY hh:mm A')
							: '',
						changedBy: [user.firstName || '', user.lastName || '']
							.filter(Boolean)
							.join(' '),
					};
				});

				this.setState({ isLoading: false, results: rows });
			},
		});
	};

	handleCopy = () => {
		const { results } = this.state;
		if (!results || !results.length) return;

		const header = COLUMNS.map((c) => c.label).join('\t');
		const rows = results.map((row) =>
			COLUMNS.map((c) => (row[c.key] != null ? row[c.key] : '')).join('\t')
		);
		const text = [header, ...rows].join('\n');

		navigator.clipboard.writeText(text).then(() => {
			this.setState({ copySuccess: true });
			setTimeout(() => this.setState({ copySuccess: false }), 2500);
		});
	};

	render() {
		const { classes, stations, authUser } = this.props;
		const { selectedStation, results, isLoading, copySuccess } = this.state;

		if (!authUser || authUser.userId !== 14) {
			return <Redirect to="/dashboard" />;
		}

		const stationOptions = sortStationsByNumber(stations).map((s) => ({
			value: s.stationId,
			display: `${s.number} - ${s.name}`,
		}));

		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<FMPHeadline label="Station History" />

					<Paper className={classes.paper}>
						<div className={classes.controls}>
							<div className={classes.autocompleteWrapper}>
								<Autocomplete
									options={stationOptions}
									value={selectedStation}
									onChange={this.handleStationChange}
									getOptionLabel={(option) => option.display}
									getOptionSelected={(option, val) => option.value === val.value}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Station"
											placeholder="Type to search..."
											fullWidth
										/>
									)}
								/>
							</div>

							<Button
								className={classes.runButton}
								variant="contained"
								color="primary"
								disabled={!selectedStation || isLoading}
								onClick={this.handleRun}
							>
								{isLoading ? <CircularProgress size={20} color="inherit" /> : 'Run'}
							</Button>
						</div>

						{results !== null && results.length === 0 && (
							<div className={classes.noResults}>No history found for this station.</div>
						)}

						{results !== null && results.length > 0 && (
							<>
								<div>
									<Button
										className={classes.copyButton}
										variant="outlined"
										size="small"
										onClick={this.handleCopy}
									>
										Copy to Clipboard
									</Button>
									{copySuccess && (
										<span className={classes.copySuccess}>Copied!</span>
									)}
								</div>

								<Table size="small" className={classes.table}>
									<TableHead>
										<TableRow>
											{COLUMNS.map((col) => (
												<TableCell key={col.key}>
													<strong>{col.label}</strong>
												</TableCell>
											))}
										</TableRow>
									</TableHead>
									<TableBody>
										{results.map((row) => (
											<TableRow key={row.stationHistoryId} hover>
												<TableCell>{row.dateCreated}</TableCell>
												<TableCell>{row.name}</TableCell>
												<TableCell className={classes.oldValue}>
													{row.oldValue}
												</TableCell>
												<TableCell className={classes.newValue}>
													{row.newValue}
												</TableCell>
												<TableCell>{row.changedBy}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</>
						)}
					</Paper>
				</Grid>
			</Grid>
		);
	}
}

StationHistory.propTypes = {
	classes: PropTypes.object.isRequired,
	stations: PropTypes.array.isRequired,
	authUser: PropTypes.object,
	searchStations: PropTypes.func.isRequired,
	searchStationHistory: PropTypes.func.isRequired,
};

export default _.flow([withStyles(styles), StationHistoryContainer])(StationHistory);
